import { AnimatedView } from '@unistocks/components/basic/animated-view';
import { medium } from '@unistocks/shared/animations';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { LayoutChangeEvent } from 'react-native';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  WithTimingConfig
} from 'react-native-reanimated';
import { View, ViewProps } from 'tamagui';

export type HeightAnimatorProps = PropsWithChildren<Omit<ViewProps, 'height'>> & {
  /**
   * Animates the height of the content from 0 to its actual height on mount
   * @default true
   */
  initial?: boolean;
  enabled?: boolean;
  debounce?: boolean | number;
  animateFromTop?: boolean;
  animationConfig?: WithTimingConfig;
  contentContainerProps?: ViewProps;
};

export const HeightAnimator: React.FC<HeightAnimatorProps> = ({
  children,
  initial = true,
  enabled = true,
  debounce,
  animateFromTop,
  animationConfig = medium,
  contentContainerProps,
  ...rest
}) => {
  const debounceTime = typeof debounce === 'number' ? debounce : 100;
  const contentHeight = useSharedValue(0);
  const shouldSnap = useSharedValue(!initial);
  const pendingHeight = useRef<number | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setHeight = (value: number) => {
    if (shouldSnap.value) {
      contentHeight.value = value;
      shouldSnap.value = false;
    } else {
      contentHeight.value = withTiming(value, animationConfig);
    }
  };

  const scheduleHeight = (value: number) => {
    pendingHeight.current = value;
    if (debounceTimer.current == null) {
      setHeight(value);
    }
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      const height = pendingHeight.current;
      if (height != null && height !== contentHeight.value) {
        setHeight(height);
      }
    }, debounceTime);
  };

  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    },
    []
  );

  const position = contentHeight === undefined ? undefined : 'absolute';

  const handleLayout = (e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    scheduleHeight(height);
  };

  const animatedStyle = useAnimatedStyle(() => ({
    height: contentHeight.value
  }));

  if (!enabled) {
    return <View {...rest}>{children}</View>;
  }

  return (
    <AnimatedView {...rest} style={[{ position: 'relative', overflow: 'hidden' }, animatedStyle]}>
      <View
        position={position}
        top={animateFromTop ? 0 : undefined}
        bottom={animateFromTop ? undefined : 0}
        left={0}
        right={0}
        {...contentContainerProps}
        onLayout={handleLayout}
      >
        {children}
      </View>
    </AnimatedView>
  );
};
